import {
  ActionIcon,
  Anchor,
  Button,
  ColorInput,
  CopyButton,
  Divider,
  Group,
  HoverCard,
  InputLabel,
  LoadingOverlay,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconChevronDown,
  IconCircleArrowRight,
  IconDeviceFloppy,
  IconDownload,
  IconHelp,
  IconLoader,
  IconPlus,
} from "@tabler/icons-react";
import mockup from "../../../../assets/create-reseller.png";
import { useEffect, useMemo, useState } from "react";
import { useProfileStore, useSessionStore } from "../../../../stores/UserStore";
import ImageGetter from "../../resellers/ImageGetter";
import { useLanguage } from "../../../../stores/LanguageStore";
import { createReseller, editReseller } from "../../../../api/UserAPI";
import StepLoad from "../../resellers/onboarding/StepLoad";
import { notifications } from "@mantine/notifications";
import ResellerCreatedModal from "./ResellerCreatedModal";
import { useNavigate } from "react-router-dom";
import { downloadExtension } from "../../../../api/ResellerAPI";
import IconCheckmark from "../../../../assets/icons/IconCheckmark";

const DefaultContent = ({ active, setActive }) => {
  const lang = useLanguage((s) => s.language);
  if (active !== "def") {
    return;
  }
  return (
    <Paper
      className="animate__animated animate__fadeInRight animate__faster"
      component={Stack}
      // justify="center"
      w={550}
      mx={"auto"}
      style={{
        borderRadius: 32,
      }}
      mt={16}
      bg={"transparent"}
    >
      <Title order={4} fw={500} ta={"left"}>
        {lang.white_labels.def_title.replace(
          "$",
          useSessionStore.getState().reseller?.name
        )}
      </Title>
      <img
        src={mockup}
        style={{
          width: "100%",
          boxShadow: "#00000016 0 2px 6px",
          borderRadius: 24,
        }}
      />

      <Group justify="center" mt={24}>
        <Button
          size="lg"
          radius={"md"}
          w={350}
          fz={18}
          leftSection={<IconCircleArrowRight size={28} />}
          onClick={() => setActive("name")}
        >
          {lang.white_labels.def_button}
        </Button>
      </Group>
    </Paper>
  );
};

const NameContent = ({ active, setActive, name, setName }) => {
  const lang = useLanguage((s) => s.language);
  const handleNext = () => {
    setActive("branding");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);

    setName(data.name);

    handleNext();
  };

  if (active !== "name") {
    return;
  }
  return (
    <Paper
      className="animate__animated animate__fadeIn"
      component={Stack}
      w={"90%"}
      style={{
        borderRadius: 32,
      }}
      mt={16}
      justify="center"
      h={"100%"}
      bg={"transparent"}
    >
      <form onSubmit={handleSubmit}>
        <TextInput
          label={lang.white_labels.name_label}
          size="lg"
          name="name"
          defaultValue={name}
          required
          placeholder={lang.white_labels.name_placeholder}
          autoFocus
          styles={{
            label: {
              marginBottom: 8,
            },
          }}
        />

        <Group justify="space-between" mt={32}>
          <Button onClick={() => setActive("def")} variant="subtle" size="md">
            {lang.global.cancel}
          </Button>
          <Button
            size="md"
            radius={"md"}
            w={175}
            fz={18}
            type="submit"
            rightSection={<IconCircleArrowRight size={28} />}
          >
            {lang.global.next}
          </Button>
        </Group>
      </form>
    </Paper>
  );
};

const BrandingContent = ({
  active,
  setActive,
  setLogo,
  setLogoMenu,
  setFavicon,
  logo,
  logoMenu,
  favicon,
  colorPrimary,
  setColorPrimary,
  colorSecondary,
  setColorSecondary,
}) => {
  const lang = useLanguage((s) => s.language);
  const [current, setCurrent] = useState(0);

  const handleNext = () => {
    if (current === 1) setActive("domains");
    else setCurrent(1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);

    if (current === 1) {
      setColorPrimary(data.color_primary);
      setColorSecondary(data.color_secondary);
    }

    handleNext();
  };

  useEffect(() => {
    setCurrent(0);
  }, []);

  if (active !== "branding") {
    return;
  }
  return (
    <Paper
      className="animate__animated animate__fadeIn"
      component={Stack}
      w={"90%"}
      style={{
        borderRadius: 32,
      }}
      mt={16}
      justify="center"
      h={"100%"}
      bg={"transparent"}
    >
      {current === 0 ? (
        <form onSubmit={handleSubmit}>
          <Text size="lg" fw={600} mb={16}>
            {lang.white_labels.add_logo}
          </Text>
          <Group justify="space-between" align="start" wrap="nowrap" gap={"lg"}>
            <ImageGetter
              tip_src={lang.resellers.onboarding.step2.logo}
              title={
                <Group gap={"xs"}>
                  <InputLabel fw={500} fz={15}>
                    {lang.white_labels.logo_main}
                  </InputLabel>
                </Group>
              }
              name={"logo"}
              types={["JPG", "JPEG", "PNG", "SVG"]}
              defaultFile={logo}
              defaultPreview={logo}
              setFile={setLogo}
              flex={1}
            />

            <ImageGetter
              tip_src={lang.resellers.onboarding.step2.logo_menu}
              title={<Text fw={500}>{lang.white_labels.logo_menu}</Text>}
              name={"logo_menu"}
              types={["JPG", "JPEG", "PNG", "SVG"]}
              defaultFile={logoMenu}
              defaultPreview={logoMenu}
              setFile={setLogoMenu}
              flex={1}
            />

            <ImageGetter
              tip_src={lang.resellers.onboarding.step2.favicon}
              title={<Text fw={500}>Favicon</Text>}
              name={"favicon"}
              defaultFile={favicon}
              defaultPreview={favicon}
              types={["ICO"]}
              isIco
              setFile={setFavicon}
              flex={1}
            />
          </Group>
          <Group justify="space-between" mt={32}>
            <Button
              onClick={() => setActive("name")}
              variant="subtle"
              size="md"
            >
              {lang.global.back}
            </Button>
            <Button
              size="md"
              radius={"md"}
              w={175}
              fz={18}
              type="submit"
              rightSection={<IconCircleArrowRight size={28} />}
            >
              {lang.global.next}
            </Button>
          </Group>
        </form>
      ) : (
        <form onSubmit={handleSubmit}>
          <Group w={"100%"} gap="lg" wrap="nowrap">
            <ColorInput
              name="color_primary"
              label={lang.resellers.create.dialog.primary_color}
              defaultValue={colorPrimary}
              size="md"
              w={"100%"}
              format="hex"
              swatches={[
                "#2e2e2e",
                "#868e96",
                "#fa5252",
                "#e64980",
                "#be4bdb",
                "#7950f2",
                "#4c6ef5",
                "#228be6",
                "#15aabf",
                "#12b886",
                "#40c057",
                "#82c91e",
                "#fab005",
                "#fd7e14",
              ]}
            />
            <ColorInput
              name="color_secondary"
              w={"100%"}
              size="md"
              label={lang.resellers.create.dialog.secondary_color}
              defaultValue={colorSecondary}
              format="hex"
              swatches={[
                "#2e2e2e",
                "#868e96",
                "#fa5252",
                "#e64980",
                "#be4bdb",
                "#7950f2",
                "#4c6ef5",
                "#228be6",
                "#15aabf",
                "#12b886",
                "#40c057",
                "#82c91e",
                "#fab005",
                "#fd7e14",
              ]}
            />
          </Group>

          <Group justify="space-between" mt={32}>
            <Button onClick={() => setCurrent(0)} variant="subtle" size="md">
              {lang.global.back}
            </Button>
            <Button
              size="md"
              radius={"md"}
              w={175}
              fz={18}
              type="submit"
              rightSection={<IconCircleArrowRight size={24} />}
            >
              {lang.global.next}
            </Button>
          </Group>
        </form>
      )}
    </Paper>
  );
};

const DomainsContent = ({
  active,
  setActive,
  domain,
  setDomain,
  setSubmitting,
  created,
}) => {
  const lang = useLanguage((s) => s.language);
  const handleSubmit = (e) => {
    e.preventDefault();
    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);

    setDomain(data.domain);

    setSubmitting(true);
  };

  if (active !== "domains") {
    return;
  }
  return (
    <Paper
      className="animate__animated animate__fadeIn"
      component={Stack}
      w={"90%"}
      style={{
        borderRadius: 32,
      }}
      mt={16}
      justify="center"
      h={"100%"}
      bg={"transparent"}
    >
      <form onSubmit={handleSubmit} style={{ position: "relative" }}>
        <HoverCard width={450} position="top">
          <HoverCard.Target>
            <Button
              variant="light"
              h={36}
              w={36}
              px={0}
              radius={"lg"}
              color="secondary"
              pos={"absolute"}
              top={-8}
              left={lang.id === "FRA" ? 380 : 290}
            >
              <IconHelp size={24} />
            </Button>
          </HoverCard.Target>

          <HoverCard.Dropdown>
            <Text>
              {lang.white_labels.domains_text}
              <CopyButton value={"resellers-pool1.magileads.net"}>
                {({ copied, copy }) => (
                  <Tooltip
                    label={copied ? lang.global.copied : lang.global.copy}
                    color={copied ? "green" : "black"}
                  >
                    <Anchor
                      c={"black"}
                      onClick={copy}
                      truncate="end"
                      fw={600}
                      ml={12}
                    >
                      {"resellers-pool1.magileads.net"}
                    </Anchor>
                  </Tooltip>
                )}
              </CopyButton>
            </Text>
          </HoverCard.Dropdown>
        </HoverCard>
        <TextInput
          label={lang.white_labels.domain_label}
          size="lg"
          name="domain"
          required
          placeholder="sub.domain.ltd"
          defaultValue={domain}
          autoFocus
          styles={{
            label: {
              marginBottom: 8,
            },
          }}
        />

        {!created && (
          <Group justify="space-between" mt={32}>
            <Button
              onClick={() => setActive("branding")}
              variant="subtle"
              size="md"
            >
              {lang.global.back}
            </Button>
          </Group>
        )}

        {created ? (
          <Group justify="space-between" mt={32}>
            <Button
              onClick={() => setActive("branding")}
              variant="subtle"
              size="md"
            >
              {lang.global.back}
            </Button>

            <Button
              size="md"
              radius={"md"}
              w={275}
              fz={18}
              type="submit"
              leftSection={<IconDeviceFloppy size={24} />}
            >
              {lang.global.save}
            </Button>
          </Group>
        ) : (
          <Group justify="space-between" mt={32}>
            <Button
              size="md"
              radius={"md"}
              w={275}
              fz={18}
              type="button"
              variant="light"
              leftSection={<IconDeviceFloppy size={24} />}
              onClick={() => setSubmitting(true)}
            >
              {lang.global.save}
            </Button>

            <Button
              size="md"
              radius={"md"}
              w={275}
              fz={18}
              type="submit"
              leftSection={<IconPlus size={24} />}
            >
              {lang.white_labels.create_button}
            </Button>
          </Group>
        )}
      </form>
    </Paper>
  );
};

const ExtensionContent = ({ active, created, setActive }) => {
  const lang = useLanguage((s) => s.language);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();
  const handleNext = () => {
    if (created) {
      navigate("/resellers/" + created);
    } else {
      navigate("/resellers/");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);

    const formData = new FormData();

    formData.set("link_chrome_extension", data.link_chrome_extension);

    setLoading(true);
    editReseller({ id: created, formData })
      .then(() => {
        handleNext();
      })
      .finally(() => setLoading(false));
  };

  const handleDownload = () => {
    setDownloading(true);
    downloadExtension({
      id: created,
      name: "Chrome Extension #" + created,
    })
      .catch((err) => {
        console.log(
          err.response.data.text().then((t) => {
            const json = JSON.parse(t);

            if (json.state_message === "empty_logo") {
              setActive("branding");
            }
            if (json.state_message === "empty_domain_saas") {
              setActive("domains");
            }
          })
        );
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  if (active !== "extension") {
    return;
  }
  return (
    <Paper
      className="animate__animated animate__fadeIn"
      component={Stack}
      w={"90%"}
      style={{
        borderRadius: 32,
      }}
      mt={16}
      justify="center"
      h={"100%"}
      bg={"transparent"}
    >
      <form onSubmit={handleSubmit}>
        <Button
          leftSection={<IconDownload />}
          size="lg"
          w={"100%"}
          onClick={handleDownload}
          loading={downloading}
        >
          {lang.white_labels.download_ext}
        </Button>

        <Divider label="Puis" my={32} styles={{ label: { fontSize: 12 } }} />

        <TextInput
          label={lang.white_labels.add_ext_link}
          size="lg"
          name="link_chrome_extension"
          placeholder="https://chromewebstore.google.com/..."
          autoFocus
          styles={{
            label: {
              marginBottom: 8,
            },
          }}
          mb={16}
          required
        />

        {useSessionStore.getState().reseller?.name === "Magileads" && (
          <Anchor
            href="https://app.files-magileads.net/file/6670101BCD2B2737686486/Proc%C3%A9dure%20pour%20uploader%20votre%20extension%20sur%20le%20Google%20Chrome%20Store.pdf"
            target="_blank"
          >
            {lang.white_labels.view_tuto}
          </Anchor>
        )}

        <Group justify="end" mt={32} gap={"xl"}>
          <Button
            onClick={() => navigate("/resellers")}
            size="md"
            radius={"md"}
            w={200}
            fz={18}
            type="submit"
            variant="light"
          >
            {lang.white_labels.add_later}
          </Button>

          <Button
            size="md"
            radius={"md"}
            w={200}
            fz={18}
            type="submit"
            leftSection={<IconCheck size={28} />}
            loading={loading}
          >
            {lang.global.finish}
          </Button>
        </Group>
      </form>
    </Paper>
  );
};

const CreateReseller = () => {
  const lang = useLanguage((s) => s.language);
  const [active, setActive] = useState("name");
  const [activeIndex, setActiveIndex] = useState(0);
  const [openedVideo, setOpenedVideo] = useState(false);

  const [name, setName] = useState("");
  const [logo, setLogo] = useState(null);
  const [logoMenu, setLogoMenu] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [colorPrimary, setColorPrimary] = useState("#FDB713");
  const [colorSecondary, setColorSecondary] = useState("#3f51b5");
  const [domain, setDomain] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const elements = useMemo(() => {
    return {
      def: {
        done: true,
        label: lang.white_labels.def_button_label,
      },
      name: {
        done: !!name,
        label: lang.white_labels.name_button_label,
      },
      branding: {
        done: logo && logoMenu && favicon,
        label: lang.white_labels.branding_button_label,
      },
      domains: {
        done: !!domain,
        label: lang.white_labels.domains_button_label,
      },
      extension: {
        done: activeIndex > 4,
        label: lang.white_labels.extension_button_label,
      },
    };
  }, [activeIndex, domain, favicon, lang, logo, logoMenu, name]);

  useEffect(() => {
    if (submitting) {
      const formData = new FormData();

      formData.set("name", name);
      formData.set("color_primary", colorPrimary.substring(1));
      formData.set("color_secondary", colorSecondary.substring(1));
      formData.set("domain_saas3", domain);
      // formData.set("color_muted", data.color_muted.substring(1));

      formData.set("logo", logo);
      formData.set("logo_menu", logoMenu);
      formData.set("favicon", favicon);

      let main = created
        ? (fd) => editReseller({ id: created, formData: fd })
        : createReseller;

      main(formData)
        .then((res) => {
          if (res.data.state) {
            setActive("extension");
            if (!created) setCreated(res.data.reseller_id);
          }
        })
        .catch((err) => {
          let error =
            err.response.data?.errors?.[0] || err.response.data?.state_message;
          console.log(error);

          if (error === "The name must be a string.") {
            notifications.clean();
            notifications.show({
              message: lang.white_labels.name_required,
              color: "red",
            });
            setActive("name");
          }
          if (error === "incorrect_domain_saas2") {
            notifications.clean();
            notifications.show({
              message: lang.white_labels.domain_invalid,
              color: "red",
            });
            setActive("domains");
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  }, [
    colorPrimary,
    colorSecondary,
    created,
    domain,
    favicon,
    logo,
    logoMenu,
    name,
    submitting,
    lang,
  ]);

  useEffect(() => {
    setActiveIndex(Object.keys(elements).findIndex((x) => x === active));
  }, [active, elements]);

  return (
    <Stack gap={0} pr={48} pt={32} px={64} pb={16} className="get-started">
      <LoadingOverlay
        overlayProps={{ blur: 5 }}
        loaderProps={{ children: <StepLoad /> }}
        visible={submitting}
      />

      <Title order={2} ff={"Inter"} ta={"center"} w={"100%"}>
        <span style={{ fontSize: 24, marginRight: 8 }}>🎉</span>{" "}
        {lang.white_labels.welcome}{" "}
        {useProfileStore.getState()?.profile?.first_name} !
      </Title>
      <Group
        w={"100%"}
        wrap="nowrap"
        align="stretch"
        gap={0}
        pos={"relative"}
        mt={32}
      >
        <Paper
          pos={"absolute"}
          top={0}
          left={0}
          w={"100%"}
          h={"100%"}
          style={{
            transition: "all ease .3s",
            borderRadius: 24,
            background: "#fff",
            boxShadow: "#00000012 0 1px 4px",
          }}
        />
        <Stack w={"45%"} className="buttons" p={"xl"} pt={24}>
          <Title order={4} fw={500} ff={"Inter"} mb={8} pos={"relative"}>
            <span style={{ fontSize: 24, marginRight: 8 }}> 🚀</span>{" "}
            {lang.white_labels.first_step}
          </Title>
          {Object.keys(elements).map((key) => {
            let item = elements[key];

            if (key === "def") {
              return "";
            }

            if (key === "extension") {
              return (
                <Stack key={key} mt={16}>
                  <Text pos={"relative"} fw={600} ml={16}>
                    <span style={{ fontSize: 24, marginRight: 8 }}>✨</span>{" "}
                    {lang.white_labels.more}
                  </Text>
                  <Group wrap="nowrap">
                    <Button
                      size="lg"
                      w={active === key ? "100%" : "95%"}
                      justify="start"
                      radius={"lg"}
                      h={64}
                      className="bump-stroke test no-shadow"
                      variant={active === key ? "light" : "default"}
                      onClick={() => {
                        setActive(key);
                        // setActive(active === key ? "def" : key);
                      }}
                      leftSection={
                        item.done ? (
                          <ThemeIcon
                            variant="subtle"
                            color="green"
                            size={"lg"}
                            radius={"md"}
                          >
                            <IconCheckmark
                              style={{
                                width: 20,
                                height: 20,
                                fill: "var(--mantine-color-green-6)",
                              }}
                            />
                          </ThemeIcon>
                        ) : (
                          <ThemeIcon
                            variant="subtle"
                            size={"lg"}
                            radius={"md"}
                            color="secondary.6"
                          >
                            <IconLoader size={24} />
                          </ThemeIcon>
                        )
                      }
                      opacity={active === "def" ? 1 : active === key ? 1 : 0.7}
                      rightSection={
                        <IconChevronDown
                          size={24}
                          opacity={active === key ? 1 : 0.4}
                          style={{
                            transform:
                              active === key ? "rotate(-90deg)" : "none",
                            transition: "all ease .3s",
                          }}
                        />
                      }
                      styles={{
                        label: {
                          marginLeft: 16,
                        },
                      }}
                      style={{
                        border: "1px solid var(--mantine-color-primary-0)",
                        transition: "all ease .3s",
                      }}
                    >
                      {item.label}
                    </Button>
                  </Group>
                </Stack>
              );
            }

            return (
              <Button
                key={key}
                size="lg"
                w={active === key ? "100%" : "95%"}
                justify="start"
                radius={"lg"}
                h={64}
                className="bump-stroke test no-shadow"
                variant={active === key ? "light" : "default"}
                onClick={() => {
                  setActive(key);
                  // setActive(active === key ? "def" : key);
                }}
                leftSection={
                  item.done ? (
                    <ThemeIcon
                      variant="subtle"
                      color="green"
                      size={"lg"}
                      radius={"md"}
                    >
                      <IconCheckmark
                        style={{
                          width: 20,
                          height: 20,
                          fill: "var(--mantine-color-green-6)",
                        }}
                      />
                    </ThemeIcon>
                  ) : (
                    <ThemeIcon
                      variant="subtle"
                      size={"lg"}
                      radius={"md"}
                      color="secondary.6"
                    >
                      <IconLoader size={24} />
                    </ThemeIcon>
                  )
                }
                opacity={active === "def" ? 1 : active === key ? 1 : 0.7}
                rightSection={
                  <IconChevronDown
                    size={24}
                    opacity={active === key ? 1 : 0.4}
                    style={{
                      transform: active === key ? "rotate(-90deg)" : "none",
                      transition: "all ease .3s",
                    }}
                  />
                }
                styles={{
                  label: {
                    marginLeft: 16,
                  },
                }}
                style={{
                  border: "1px solid var(--mantine-color-primary-0)",
                  transition: "all ease .3s",
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Stack>

        <Stack
          w={"55%"}
          gap={0}
          pos={"relative"}
          style={{
            transition: "all ease .3s",
            // borderRadius: 24,
            // background: "#fff",
            // boxShadow: "#00000012 0 1px 4px",
            height: "65vh",
            overflow: "hidden",
            borderLeft: "1px solid var(--mantine-color-primary-0)",
            marginTop: 32,
            marginBottom: 32,
          }}
          align="center"
        >
          {/* <DefaultContent active={active} setActive={setActive} /> */}

          <NameContent
            active={active}
            setActive={setActive}
            name={name}
            setName={setName}
          />

          <BrandingContent
            active={active}
            setActive={setActive}
            setLogo={setLogo}
            setFavicon={setFavicon}
            setLogoMenu={setLogoMenu}
            colorPrimary={colorPrimary}
            setColorPrimary={setColorPrimary}
            colorSecondary={colorSecondary}
            setColorSecondary={setColorSecondary}
            logo={logo}
            logoMenu={logoMenu}
            favicon={favicon}
          />

          <DomainsContent
            active={active}
            setActive={setActive}
            domain={domain}
            setDomain={setDomain}
            setSubmitting={setSubmitting}
            created={created}
          />

          <ExtensionContent
            active={active}
            setActive={setActive}
            created={created}
          />
        </Stack>
      </Group>
      {/* 
      {active !== "def" && !openedVideo && (
        <Paper
          pos={"fixed"}
          top={128}
          right={16}
          w={200}
          h={100}
          style={{ zIndex: 4, overflow: "hidden", cursor: "pointer" }}
          radius={8}
          shadow="lg"
          className="affix-video animate__animated animate__zoomInRight"
          onClick={() => setOpenedVideo(true)}
        >
          <img src={mockup} width={"100%"} />
        </Paper>
      )} */}

      <Modal
        opened={openedVideo}
        size={"auto"}
        centered
        onClose={() => setOpenedVideo(false)}
        withCloseButton={false}
        radius={54}
        styles={{
          body: {
            padding: 8,
          },
        }}
      >
        <img src={mockup} width={1024} style={{ marginBottom: -6 }} />
      </Modal>

      {created && <ResellerCreatedModal domain={domain} />}
    </Stack>
  );
};

export default CreateReseller;
